import React from "react";
import moment from "moment";
import { graphql } from "gatsby";
import { getImage, IGatsbyImageData } from "gatsby-plugin-image";
import { Typewriter } from 'react-simple-typewriter'

// @ts-ignore
import Layout from "../components/Layout";
// @ts-ignore
import News from "../components/News";
// @ts-ignore
import BioImage from "../components/BioImage";
// @ts-ignore
import FullWidthImage from "../components/FullWidthImage";
// @ts-ignore
import IndexPageTemplateQuery from "../__generated__/gatsby-types";

interface NewsData {
  date: string;
  title: string;
  description: string;
  newsRelatedFile: {
    publicURL: string;
  };
  image: {
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData;
    };
  };
}

interface BioSection {
  bio: {
    image: {
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData;
      };
    };
    description: string;
    imageAlt: string;
    linkTo: string;
  };
  projects: {
    image: {
      childImageSharp: {
        gatsbyImageData: IGatsbyImageData;
      };
    };
    description: string;
    imageAlt: string;
    linkTo: string;
  };
}

interface IndexPageProps {
  data: {
    markdownRemark: {
      frontmatter: {
        title: string;
        language: string;
        image: IGatsbyImageData | string;
        quote: string;
        news: NewsData[];
        biosection: BioSection;
      };
    };
  };
}

type ImageDataOrString = IGatsbyImageData | string

const heroImageGetter = (image: ImageDataOrString) => {
  let returnImage = image
  if (typeof image === "object") {
    let gotImage = getImage(image) // only call getImage if image is of type IGatsbyImageData
    returnImage = gotImage ?? returnImage // only set if not undefined
  }
  return returnImage
}

export const IndexPageTemplate = ({
  image,
  title,
  quote,
  news,
  biosection,
}: {
  image: ImageDataOrString;
  title: string;
  quote: string;
  news: NewsData[];
  biosection: BioSection;
}): JSX.Element => {

  news.sort((a: NewsData, b: NewsData) => moment(b.date, "DD-MM-YYYY").toDate().getTime() - moment(a.date, "DD-MM-YYYY").toDate().getTime());
  const heroImage: ImageDataOrString = heroImageGetter(image);

  return (
    <div>
      <div className="customContainer">
        <FullWidthImage img={heroImage} title={title} />
        <div className="columns atSeventyFivePercentHeight">
          <div className="column is-4">
            <h1 className="title has-text-white">
              <Typewriter
                words={["", "Sebastian", ""]}
                typeSpeed={100}
                delaySpeed={5000}
              />
            </h1>
          </div>
          <div className="column is-4"></div>
          <div className="column is-4">
            <h1 className="title has-text-white">
              <Typewriter
                words={["", "Oberlin", ""]}
                typeSpeed={100}
                delaySpeed={5000}
              />
            </h1>
          </div>
        </div>
      </div>
      <div className="content">
        <section className="section section--gradient is-large">
          <div className="columns">
            <div className="column is-6 is-offset-3">
              <div className="quote">
                <h3 className="">{quote}</h3>
              </div>
            </div>
          </div>
        </section>
        <section className="section section--gradient is-small has-background-dark has-text-white-ter">
          <h1 className="title is-1 isShadows ml-5">News</h1>
          <div className="columns">
            <div className="column is-10 is-offset-1">
              <News gridItems={news} />
            </div>
          </div>
        </section>
        <section className="section section--gradient is-medium">
          <div className="bioSection columns mt-6">
            <BioImage {...biosection.bio} />
            <BioImage {...biosection.projects} />
          </div>
        </section>
      </div>
    </div>
  );
};

interface IndexPageProps {
  data: IndexPageTemplateQuery;
}

const IndexPage: React.FC<IndexPageProps> = ({ data }) => {
  const { frontmatter } = data.markdownRemark;

  return (
    <Layout language={frontmatter.language}>
      <IndexPageTemplate
        image={frontmatter.image}
        title={frontmatter.title}
        quote={frontmatter.quote}
        news={frontmatter.news}
        biosection={frontmatter.biosection}
      />
    </Layout>
  );
};

export default IndexPage;

export const pageQuery = graphql`
query IndexPageTemplate($id: String!) {
  markdownRemark(id: { eq: $id }) {
    frontmatter {
      title
      language
      image {
        childImageSharp {
          gatsbyImageData(
            height: 1024
            quality: 100
            placeholder: BLURRED
          )
        }
      }
      quote
      news {
        date(formatString: "DD-MM-YYYY")
        title
        description
        newsRelatedFile {
          publicURL
        }
        image {
          childImageSharp {
            gatsbyImageData(width: 480, quality: 64, layout: CONSTRAINED)
          }
        }
      }
      biosection {
        bio {
          image {
            childImageSharp {
              gatsbyImageData(width: 480, aspectRatio: 1.33, quality: 64, layout: CONSTRAINED)
            }
          }
          description
          imageAlt
          linkTo
        }
        projects {
          image {
            childImageSharp {
              gatsbyImageData(width: 480, aspectRatio: 1.33, quality: 64, layout: CONSTRAINED)
            }
          }
          description
          imageAlt
          linkTo
        }
      }
    }
  }
}`;
